import * as Dialog from '@radix-ui/react-dialog';
import RadixModalHeader from './RadixModalHeader';
import { MessageSquare } from 'lucide-react';
import MessageTabs from './MessageTabs';

/**
 * WHY THIS IS CENTRED WITH A FLEX BOX AND NOT WITH A TRANSFORM.
 *
 * It used to be the Radix idiom —
 * `fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2` — and every
 * spotlight in the 22-step messaging tour was 164px above the control it was
 * supposed to be a hole over. Measured at 1440x900 with
 * `scripts/probe-tour-placement.mjs 'harness.html?view=messaging'
 * '#start-messaging-tour-button'`: dx 0, dy -164 on all 21 spotlit steps.
 *
 * The 164 is this dialog's own distance from the top of the window, and it is
 * react-joyride that puts it there. Its Overlay positions the spotlight
 * `fixed` — viewport coordinates — whenever any ancestor of the target is
 * `position: fixed`, which is true inside any modal. But the TOP it gives it
 * comes from `getElementPosition`, which for a target that also sits in a
 * custom scroll parent returns `element.offsetTop - scrollParent.scrollTop`
 * (modules/dom.ts). `offsetTop` is measured from the nearest POSITIONED
 * ancestor, so the two agree only when that ancestor's own top is at viewport
 * 0. The composer's scroll parent is the `overflow-y-auto` pane inside
 * MessageTemplateEditor, so this took that branch, and the positioned ancestor
 * was this dialog — sitting halfway down the screen. Hence the offset, in
 * full: viewport-relative placement fed an offsetParent-relative number.
 *
 * The record shell has never had this bug for the same reason: `.form-modal` is
 * `position: fixed; top: 0; left: 0` with a static `.rec-modal` inside it, so
 * its positioned ancestor IS at the viewport origin and `offsetTop` happens to
 * be the viewport top. This now matches that shape: a full-screen fixed frame
 * that centres a STATICALLY positioned panel.
 *
 * `transform: none` IS PART OF THE FIX, not tidying. carstyles.css has a
 * global `[role="dialog"] { transform: translateZ(0); backface-visibility:
 * hidden; }` — an old GPU-promotion hack — and a transformed element is a
 * containing block for absolutely-positioned descendants, which by the HTML
 * spec's own definition makes it their `offsetParent` even at `position:
 * static`. So flex-centring alone changed nothing: the probe still read
 * dy -164 with the panel unpositioned, because the identity transform was
 * still holding the offsetParent here. The backface-visibility half stays; it
 * is not a containing block and it keeps the compositor hint.
 *
 * THREE THINGS TO KEEP, then. The frame must stay `inset-0` — a frame inset by
 * a gutter reintroduces the offset, by exactly the gutter. The panel must stay
 * unpositioned: giving it `relative` for a z-index makes IT the offsetParent
 * again and the whole thing comes back; it needs none, because a child paints
 * over its parent's background without help. And the panel must stay
 * untransformed, for the same reason.
 *
 * `pointer-events: none` on the frame is what keeps the dimmed backdrop
 * clickable through it — the frame covers the overlay, and Radix decides
 * "outside" from the pointer events the overlay receives.
 *
 * src/messagingModal.test.jsx holds all three as assertions, and
 * src/tourCoverage.test.js holds the general rule for every toured dialog.
 */
export default function MessageSelected({
  isOpen=true,
  onClose,
  recordsData = [],
  sheetName
}) {
  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40" style={{ background: 'rgba(15, 23, 42, 0.55)' }} />
        <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
          <Dialog.Content
            id="messaging-modal"
            className="pointer-events-auto p-0 overflow-hidden w-full max-w-5xl flex flex-col max-h-[95vh]"
            style={{ background: 'var(--theme-surface)', borderRadius: 'var(--theme-radius-lg)', boxShadow: 'var(--theme-shadow-dialog)', transform: 'none', backfaceVisibility: 'hidden' }}
            onPointerDownOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <RadixModalHeader dialog={Dialog}
              icon={MessageSquare}
              title="Messages"
              subtitle="Compose and send SMS messages to your customers"
              actions={recordsData.length > 0 && (
                <span className="pill-t pill-t-accent">{recordsData.length} Customers Selected</span>
              )}
            />

            <div className="flex-grow overflow-hidden px-6 py-4">
              <MessageTabs recordsData={recordsData} onClose={onClose} sheetName={sheetName} />
            </div>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
